import React from 'react';
import {
  Plus, Settings, HelpCircle, ChevronDown,
  Palette, Sparkles, FileText, FolderSync,
  Sliders, Type, Table as TableIcon, QrCode,
  Barcode as BarcodeIcon, CheckCircle, Hash,
  Grid, Image as ImageIcon, Search,
} from 'lucide-react';

import { ElementType, CanvasElement, PageSizePreset, PageConfig } from '../types';
import { TEMPLATE_PRESETS } from '../data/templates';
import { THERMAL_PRESETS_GENERATORS } from '../data/thermalTemplates';
import { INDUSTRY_CATEGORIES_METADATA } from '../data/industryCategories';
import { PAGE_PRESETS } from '../types';
import CurrencySelector from './CurrencySelector';
import { getCurrencySymbol } from '../utils/currency';
import {
  isInvoiceMobileEmbed,
  sendInvoiceMobileAction,
} from '../utils/invoiceMobileBridge';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MarketplaceProfile {
  businessName: string;
  representative: string;
  license: string;
  contact: string;
  address: string;
  terms: string;
  color: string;
}

interface ActiveTemplate {
  name: string;
  elements: CanvasElement[];
  pageSize: PageSizePreset;
}

interface LeftSidebarProps {
  // tab
  activeTab: 'add' | 'page' | 'guide';
  setActiveTab: (v: 'add' | 'page' | 'guide') => void;

  // panels open state
  openPanels: Record<string, boolean>;
  togglePanel: (key: string) => void;

  // brand color
  corporateColor: string;
  setCorporateColor: (v: string) => void;
  applyThemeColor: (hex: string) => void;
  applyThemeColorDebounced: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;

  // marketplace
  activeMarketplaceProfile: 'agent' | 'agency' | 'builder' | 'developer';
  setActiveMarketplaceProfile: (v: 'agent' | 'agency' | 'builder' | 'developer') => void;
  marketplaceProfiles: Record<string, MarketplaceProfile>;
  updateMarketplaceProfile: (key: keyof MarketplaceProfile, val: string) => void;
  applyMarketplaceBranding: (role?: string) => void;
  setSelectedTemplateCategory: (v: string) => void;

  // templates
  selectedTemplateCategory: string;
  customTemplates: ActiveTemplate[];
  setCustomTemplates: (v: ActiveTemplate[]) => void;
  loadTemplate: (idx: number) => void;
  handleLoadTemplateObj: (t: ActiveTemplate) => void;

  // industry library
  industrySearchQuery: string;
  setIndustrySearchQuery: (v: string) => void;
  selectedIndustryGroup: string;
  setSelectedIndustryGroup: (v: string) => void;
  filtered180Niches: typeof INDUSTRY_CATEGORIES_METADATA;
  generateIndustryTemplate: (name: string, pageSize: PageSizePreset) => ActiveTemplate;
  setActiveIndustryGroup: (v: string) => void;
  activeIndustryName: string;
  setActiveIndustryName: (v: string) => void;

  // components
  handleAddElement: (type: ElementType, customProps?: Partial<CanvasElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // draw mode
  isDrawingMode: boolean;
  setIsDrawingMode: (v: boolean) => void;
  setSelectedElementId: (v: string | null) => void;
  currentPenColor: string;
  setCurrentPenColor: (v: string) => void;
  currentPenWidth: number;
  setCurrentPenWidth: (v: number) => void;

  // page settings
  pageSize: PageSizePreset;
  setPageSize: (v: PageSizePreset) => void;
  pageConfig: PageConfig;
  pageBehavior: 'fixed' | 'auto_expand' | 'multi_page';
  setPageBehavior: (v: 'fixed' | 'auto_expand' | 'multi_page') => void;
  gridSnapping: boolean;
  computedHeight: number;
  elements: CanvasElement[];
  setElements: (v: CanvasElement[]) => void;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
  setZoom: (v: number) => void;

  // currency
  activeCurrency: string;
  currencyDisplayMode: 'symbol' | 'code';
  handleCurrencyChange: (v: any) => void;
  handleCurrencyDisplayModeChange: (v: 'symbol' | 'code') => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  activeTab, setActiveTab,
  openPanels, togglePanel,
  corporateColor, setCorporateColor, applyThemeColor, applyThemeColorDebounced,
  activeMarketplaceProfile, setActiveMarketplaceProfile,
  marketplaceProfiles, updateMarketplaceProfile, applyMarketplaceBranding,
  setSelectedTemplateCategory, selectedTemplateCategory,
  customTemplates, setCustomTemplates, loadTemplate, handleLoadTemplateObj,
  industrySearchQuery, setIndustrySearchQuery,
  selectedIndustryGroup, setSelectedIndustryGroup,
  filtered180Niches, generateIndustryTemplate,
  setActiveIndustryGroup, activeIndustryName, setActiveIndustryName,
  handleAddElement, fileInputRef, handleImageUpload,
  isDrawingMode, setIsDrawingMode, setSelectedElementId,
  currentPenColor, setCurrentPenColor,
  currentPenWidth, setCurrentPenWidth,
  pageSize, setPageSize, pageConfig, pageBehavior, setPageBehavior,
  gridSnapping, computedHeight, elements, setElements, saveToHistory, setZoom,
  activeCurrency, currencyDisplayMode, handleCurrencyChange, handleCurrencyDisplayModeChange,
}) => {
    const mobileEmbed = isInvoiceMobileEmbed();

  const runOrSend = (
    action: string,
    payload: Record<string, any>,
    webAction: () => void,
  ) => {
    if (mobileEmbed) {
      const sent = sendInvoiceMobileAction({
        action,
        ...payload,
      });

      if (sent) return;
    }

    webAction();
  };

  const mobileAddElement = (
    type: ElementType,
    customProps: Partial<CanvasElement> = {},
  ) => {
    runOrSend(
      'add-element',
      {
        elementType: type,
        props: customProps,
      },
      () => handleAddElement(type, customProps),
    );
  };

  const mobileHandleImageUpload = (
  e: React.ChangeEvent<HTMLInputElement>,
) => {
  // Normal website keeps using the original upload logic.
  if (!mobileEmbed) {
    handleImageUpload(e);
    return;
  }

  const file = e.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = (event) => {
    const base64 = event.target?.result as string;

    if (!base64) return;

    mobileAddElement('image', {
      textContent: base64,
      width: 160,
      height: 120,
    });
  };

  reader.readAsDataURL(file);
};
  const mobileApplyThemeColor = (color: string) => {
  // Immediately show the selected color in the mobile UI
  setCorporateColor(color);

  // Send to mobile canvas, or execute directly on desktop
  runOrSend(
    'apply-theme-color',
    { color },
    () => applyThemeColor(color),
  );
};
const mobileSetDrawingMode = (enabled: boolean) => {
  setIsDrawingMode(enabled);
  setSelectedElementId(null);

  runOrSend(
    'set-drawing-mode',
    { enabled },
    () => {
      // Desktop/web state was already updated above.
    },
  );
};
const mobileSetPenColor = (color: string) => {
  setCurrentPenColor(color);

  runOrSend(
    'set-pen-color',
    { color },
    () => {
      // Website state already updated above.
    },
  );
};
const mobileSetPenWidth = (width: number) => {
  setCurrentPenWidth(width);

  runOrSend(
    'set-pen-width',
    { width },
    () => {
      // Website state already updated above.
    },
  );
};
const mobileCurrencyChange = (raw: any) => {
  const currency =
    typeof raw === 'string'
      ? raw
      : (raw?.iso_code || raw?.code || 'USD');

  // Update sidebar UI immediately
  handleCurrencyChange(currency);

  runOrSend(
    'set-currency',
    { currency },
    () => {
      // Already updated above on normal web
    },
  );
};

const mobileCurrencyDisplayModeChange = (
  mode: 'symbol' | 'code',
) => {
  // Update sidebar UI immediately
  handleCurrencyDisplayModeChange(mode);

  runOrSend(
    'set-currency-display-mode',
    { mode },
    () => {
      // Already updated above on normal web
    },
  );
};

  const mobileLoadTemplate = (index: number) => {
    runOrSend(
      'load-template',
      { templateIndex: index },
      () => loadTemplate(index),
    );
  };

  const mobileLoadTemplateObject = (
  template: ActiveTemplate,
  industryGroup?: string,
  industryName?: string,
) => {
  // Update sidebar selection immediately
  if (industryGroup) {
    setActiveIndustryGroup(industryGroup);
  }

  if (industryName) {
    setActiveIndustryName(industryName);
  }

  runOrSend(
      'load-template',
      {
        template,
        industryGroup,
        industryName,
      },
      () => {
        handleLoadTemplateObj(template);

        if (industryGroup) {
          setActiveIndustryGroup(industryGroup);
        }

        if (industryName) {
          setActiveIndustryName(industryName);
        }
      },
    );
  };

  const mobileApplyMarketplaceBranding = () => {
    runOrSend(
      'apply-marketplace-branding',
      {
        profile: activeMarketplaceProfile,
        profileData:
          marketplaceProfiles[activeMarketplaceProfile],
      },
      () => applyMarketplaceBranding(),
    );
  };

  const BRAND_COLORS = [
    { hex: '#4f46e5', title: 'Indigo' }, { hex: '#1e3a8a', title: 'Navy' },
    { hex: '#10b981', title: 'Emerald' }, { hex: '#dc2626', title: 'Crimson' },
    { hex: '#0284c7', title: 'Sky Blue' }, { hex: '#b91c1c', title: 'Burgundy' },
    { hex: '#ea580c', title: 'Orange' }, { hex: '#0f172a', title: 'Slate' },
    { hex: '#ffffff', title: 'White' },
  ];

  const THEME_PRESETS = [
    { name: 'Indigo Premium', hex: '#4f46e5', label: 'Tech & SaaS' },
    { name: 'Corporate Navy', hex: '#1e3a8a', label: 'Financial' },
    { name: 'Emerald Growth', hex: '#10b981', label: 'Eco & Agri' },
    { name: 'Sleek Charcoal', hex: '#1e293b', label: 'Minimalist' },
    { name: 'Rose Tech', hex: '#dc2626', label: 'Creative' },
    { name: 'Teal Health', hex: '#0d9488', label: 'Institutional' },
    { name: 'Warm Amber', hex: '#d97706', label: 'Contractor' },
    { name: 'Royal Plum', hex: '#9333ea', label: 'Premium' },
  ];

  const [sidebarWidth, setSidebarWidth] = React.useState(420);

// UI feedback for Ready-Made Presets
const [selectedPresetIndex, setSelectedPresetIndex] =
  React.useState<number | null>(null);
  const isResizing = React.useRef(false);

  const handleResizeMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
  isResizing.current = true;
  e.preventDefault();
  const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
  const startWidth = sidebarWidth;
  const onMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing.current) return;
    const point = 'touches' in e ? e.touches[0] : e;
    if (!point) return;
    const newWidth = Math.min(600, Math.max(240, startWidth + (point.clientX - startX)));
    setSidebarWidth(newWidth);
  };
  const onMouseUp = () => {
    isResizing.current = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('touchmove', onMouseMove);
    window.removeEventListener('touchend', onMouseUp);
  };
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('touchmove', onMouseMove, { passive: false });
  window.addEventListener('touchend', onMouseUp);
};

  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .sidebar-resize-handle-left { display: none !important; }
        .left-sidebar-panel { width: 100% !important; }
      }
    `}</style>
    <aside 
      className="left-sidebar-panel bg-white border-r border-slate-200 flex flex-col shrink-0 z-10 relative" 
      style={{ 
        width: sidebarWidth,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
  {/* Drag handle */}
  <div
    onMouseDown={handleResizeMouseDown}
    onTouchStart={handleResizeMouseDown}
    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 50 }}
    className="sidebar-resize-handle-left group hover:bg-blue-500/20 transition-colors"
  >
        <div style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', width: 3, height: 48, borderRadius: 99, background: '#cbd5e1' }}
          className="group-hover:bg-blue-400 transition-colors" />
      </div>

      {/* Tab bar */}
      <div className="grid grid-cols-3 border-b border-slate-200 text-center text-xs shrink-0 bg-slate-50">
        {([
          { key: 'add',  label: 'Components',    Icon: Plus },
          { key: 'page', label: 'Layout / Setup', Icon: Settings },
          { key: 'guide',label: 'User Manual',    Icon: HelpCircle },
        ] as const).map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`py-3.5 font-bold transition-all border-b-2 flex flex-col items-center gap-1 ${
              activeTab === key ? 'text-blue-600 border-blue-600 bg-white' : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6">

        {/* ── TAB 1: ADD COMPONENTS ── */}
        {activeTab === 'add' && (
          <div className="space-y-6">

            {/* PANEL: Global Brand Accent */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('brand')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <Palette className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Global Brand Accent</span>
                  <span className="text-[8px] bg-blue-600 text-white font-bold px-1.5 py-0.5 rounded">Active</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.brand ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.brand && (
                <div className="p-3.5 space-y-2.5 bg-blue-50/30">
                  <p className="text-[10px] text-slate-500 leading-normal font-medium">
                    Set a primary corporate brand color. It propagates to all table headers, stamps, signatures and shape accents.
                  </p>
                  <div className="flex items-center gap-1.5">
                    <div className="relative flex items-center justify-center shrink-0 w-8 h-8 rounded-lg overflow-hidden border border-slate-300 shadow-sm">
                      <input type="color" value={corporateColor} onChange={(e) => {
                        const val = e.target.value;
                        setCorporateColor(val);
                        if (applyThemeColorDebounced.current) clearTimeout(applyThemeColorDebounced.current);
                        applyThemeColorDebounced.current = setTimeout(() => mobileApplyThemeColor(val), 80);
                      }} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold pointer-events-none" style={{ backgroundColor: corporateColor }}>🎨</div>
                    </div>
                    <input
                      type="text"
                      value={corporateColor}
                      onChange={(e) => { const val = e.target.value; setCorporateColor(val); if (/^#[0-9A-Fa-f]{6}$/.test(val)) mobileApplyThemeColor(val); }}
                      className="flex-1 min-w-0 bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-lg font-mono font-extrabold text-slate-800 focus:outline-none uppercase shadow-sm"
                      placeholder="#4F46E5" maxLength={7}
                    />
                    <button onClick={() => mobileApplyThemeColor(corporateColor)} className="px-3 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-bold text-xs transition-colors shadow-sm cursor-pointer">
                      Apply
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 items-center pt-1">
                    {BRAND_COLORS.map(p => (
                      <button key={p.hex} onClick={() => mobileApplyThemeColor(p.hex)}
                        className={`w-5 h-5 rounded-full border hover:scale-125 transition-all cursor-pointer ${corporateColor.toLowerCase() === p.hex.toLowerCase() ? 'border-slate-800 scale-110 ring-2 ring-blue-300' : p.hex === '#ffffff' ? 'border-slate-300' : 'border-white'}`}
                        style={{ backgroundColor: p.hex }} title={`${p.title} (${p.hex})`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* PANEL: GSOUQ Branding Hub */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('gsouq')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">GSOUQ Branding Hub</span>
                  <span className="text-[8px] bg-amber-500 text-white font-bold px-1.5 py-0.5 rounded">Marketplace</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.gsouq ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.gsouq && (
                <div className="p-3.5 space-y-3 bg-amber-50/20">
                  <p className="text-[10px] text-slate-600 leading-normal font-medium">
                    Select an entity role to load or customize its marketplace branding, then apply to your invoice.
                  </p>
                  <div className="grid grid-cols-4 gap-1 bg-slate-100/80 p-0.5 rounded-lg text-[9px] font-bold text-center">
                    {(['agent', 'agency', 'builder', 'developer'] as const).map((role) => (
                      <button key={role} onClick={() => { setActiveMarketplaceProfile(role); setSelectedTemplateCategory('Marketplace'); }}
                        className={`py-1.5 rounded transition-all capitalize cursor-pointer ${activeMarketplaceProfile === role ? 'bg-white text-slate-800 shadow-xs font-black border-b' : 'text-slate-500 hover:text-slate-800'}`}>
                        {role}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2 pt-1 border-t border-slate-200/60">
                    {(['businessName', 'representative', 'license', 'contact', 'address'] as const).map((field) => (
                      <div key={field}>
                        <label className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">{field}</label>
                        <input type="text" value={(marketplaceProfiles[activeMarketplaceProfile] as any)[field]}
                          onChange={(e) => updateMarketplaceProfile(field, e.target.value)}
                          className="w-full bg-white border border-slate-200 text-[10px] p-1.5 rounded font-bold text-slate-800 focus:outline-none focus:border-amber-400" />
                      </div>
                    ))}
                    <div>
                      <label className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Entity Color</label>
                      <div className="flex items-center gap-1">
                        <input type="color" value={marketplaceProfiles[activeMarketplaceProfile].color} onChange={(e) => updateMarketplaceProfile('color', e.target.value)} className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                        <span className="font-mono text-[9px] text-slate-500 uppercase">{marketplaceProfiles[activeMarketplaceProfile].color}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Settlement Terms</label>
                      <textarea value={marketplaceProfiles[activeMarketplaceProfile].terms} onChange={(e) => updateMarketplaceProfile('terms', e.target.value)} rows={2}
                        className="w-full bg-white border border-slate-200 text-[9.5px] p-1.5 rounded text-slate-600 leading-normal focus:outline-none focus:border-amber-400" />
                    </div>
                    <button onClick={() => mobileApplyMarketplaceBranding()}
                      className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-bold text-xs shadow-md transition-all flex items-center justify-center gap-1 cursor-pointer">
                      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                      ✨ Bind Entity Branding to Canvas
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* PANEL: Ready-Made Presets */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('templates')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Ready-Made Presets</span>
                  <span className="text-[8px] bg-slate-500 text-white font-bold px-1.5 py-0.5 rounded">{TEMPLATE_PRESETS.length}</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.templates ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.templates && (
                <div className="p-3.5 space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {['All', 'Marketplace', 'Corporate', 'Freelance', 'Retail', 'Logistics', 'Construction', 'Healthcare', 'Academic', 'POS Receipts'].map((cat) => {
                      const count = cat === 'All' ? TEMPLATE_PRESETS.length : TEMPLATE_PRESETS.filter(t => t.category === cat).length;
                      return (
                        <button key={cat} onClick={() => setSelectedTemplateCategory(cat)}
                          className={`px-2 py-0.5 rounded text-[9.5px] font-bold transition-all cursor-pointer border ${selectedTemplateCategory === cat ? 'bg-blue-600 border-blue-600 text-white shadow-xs' : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-800'}`}>
                          {cat} ({count})
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-[220px] overflow-y-auto pr-1">
                    {TEMPLATE_PRESETS
                      .map((t, idx) => ({ ...t, originalIdx: idx }))
                      .filter(t => selectedTemplateCategory === 'All' || t.category === selectedTemplateCategory)
                      .map((t) => (
                        <button key={t.originalIdx} onClick={() => {
  // Immediate visual feedback in sidebar
  setSelectedPresetIndex(t.originalIdx);

  if (t.category === 'POS Receipts') {
    const generator = THERMAL_PRESETS_GENERATORS.find((_, i) => 
      TEMPLATE_PRESETS.filter(tp => tp.category === 'POS Receipts')[i]?.name === t.name
    );
    if (generator) {
      mobileLoadTemplateObject(
        { ...generator(), category: 'POS Receipts' } as any
      );
      return;
    }
  }
  mobileLoadTemplate(t.originalIdx);
}}
                          className={`p-3 rounded-lg border text-left transition-all group flex items-center justify-between cursor-pointer ${
  selectedPresetIndex === t.originalIdx
    ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-400'
    : 'bg-slate-50/70 border-slate-200 hover:border-blue-400 hover:bg-blue-50/10'
}`}>
                          <div className="min-w-0 pr-2">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-bold text-slate-850 group-hover:text-blue-600 transition-colors truncate block">{t.name}</span>
                              <span className={`text-[8px] px-1 py-0.5 rounded font-black uppercase shrink-0 ${
                                t.category === 'Marketplace' ? 'bg-amber-100 text-amber-700' : t.category === 'Corporate' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-700'
                              }`}>{t.category}</span>
                            </div>
                            <p className="text-[9.5px] text-slate-500 font-mono mt-0.5">{PAGE_PRESETS[t.pageSize]?.name || t.pageSize}</p>
                          </div>
                          <span
  className={`text-[8px] font-black text-blue-500 shrink-0 ${
    selectedPresetIndex === t.originalIdx
      ? 'opacity-100'
      : 'opacity-70'
  }`}
>
  {selectedPresetIndex === t.originalIdx
    ? '✓ SELECTED'
    : '⚡'}
</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* PANEL: Industry Library */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('industries')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Industry Library</span>
                  <span className="text-[8px] bg-purple-600 text-white font-bold px-1.5 py-0.5 rounded">180</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.industries ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.industries && (
                <div className="p-3.5 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2 w-3 h-3 text-slate-400" />
                    <input type="text" placeholder="Search 180 industries..." value={industrySearchQuery}
                      onChange={(e) => setIndustrySearchQuery(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 text-[10px] pl-6 pr-2 py-1 focus:border-blue-400 focus:outline-none placeholder:text-slate-400 rounded-md text-slate-800" />
                  </div>
                  <select value={selectedIndustryGroup} onChange={(e) => setSelectedIndustryGroup(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 text-[9px] font-bold py-0.5 px-1 text-slate-700 rounded-md focus:outline-none">
                    <option value="All">All Clusters (180 Sectors)</option>
                    <option value="Retail & Commerce">Retail & Commerce</option>
                    <option value="Food & Hospitality">Food & Hospitality</option>
                    <option value="Medical & Wellness">Medical & Wellness</option>
                    <option value="Professional Services">Professional Advisory</option>
                    <option value="Logistics & Industrial">Logistics & Industrial</option>
                    <option value="Tech, Design & Academic">Tech & Academic</option>
                  </select>
                  <div className="grid grid-cols-1 gap-1 max-h-[190px] overflow-y-auto pr-1">
                    {filtered180Niches.map((niche, i) => (
                      <button key={i} onClick={() => {
                        const tmpl = generateIndustryTemplate(niche.name, pageSize);
                        mobileLoadTemplateObject(
                          tmpl,
                          niche.group,
                          niche.name,
                        );
                      }}
                        className={`w-full p-1.5 rounded-md border text-left transition-all flex items-center justify-between cursor-pointer group ${
  activeIndustryName === niche.name
    ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-400'
    : 'bg-slate-50/60 border-slate-200 hover:border-blue-400 hover:bg-blue-50/10'
}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: niche.primaryColor }} />
                            <span className="text-[10.5px] font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">{niche.name}</span>
                          </div>
                          <span className="text-[7.5px] text-slate-500 font-semibold block mt-0.5 ml-3 uppercase">{niche.group}</span>
                        </div>
                        <span
  className={`text-[8px] font-black text-blue-500 transition-opacity ${
    activeIndustryName === niche.name
      ? 'opacity-100'
      : 'opacity-0 group-hover:opacity-100'
  }`}
>
  {activeIndustryName === niche.name
    ? '✓ SELECTED'
    : 'LOAD'}
</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* PANEL: Custom Templates */}
            {customTemplates.length > 0 && (
              <div className="border border-emerald-200 rounded-xl overflow-hidden">
                <div onClick={() => togglePanel('customTemplates')} className="flex items-center justify-between px-3.5 py-2.5 bg-emerald-50/60 cursor-pointer hover:bg-emerald-50 transition-colors select-none">
                  <div className="flex items-center gap-2">
                    <FolderSync className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-[10px] font-extrabold text-emerald-800 uppercase tracking-wider">Custom Designs</span>
                    <span className="text-[8px] bg-emerald-600 text-white font-bold px-1.5 py-0.5 rounded">{customTemplates.length}</span>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-emerald-500 transition-transform duration-200 ${openPanels.customTemplates ? 'rotate-180' : ''}`} />
                </div>
                {openPanels.customTemplates && (
                  <div className="p-3.5">
                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                      {customTemplates.map((t, idx) => (
                        <div key={idx} className="p-2.5 bg-emerald-50/40 rounded-lg border border-emerald-150 hover:border-emerald-300 flex items-center justify-between group">
                          <button onClick={() => {
                            mobileLoadTemplateObject(
                              t,
                              'Professional Services',
                              t.name || 'Custom Design',
                            );
                          }} className="flex-1 text-left cursor-pointer">
                            <p className="text-xs font-bold text-emerald-950 group-hover:text-emerald-750 transition-colors line-clamp-1">{t.name}</p>
                            <p className="text-[10px] text-emerald-700 font-mono mt-0.5">{PAGE_PRESETS[t.pageSize]?.name || t.pageSize}</p>
                          </button>
                          <button onClick={() => { if (window.confirm('Delete this custom template?')) setCustomTemplates(customTemplates.filter((_, i) => i !== idx)); }}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-md cursor-pointer transition-colors hover:bg-rose-50">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PANEL: Insert Components */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('components')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <Sliders className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Insert Components</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.components ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.components && (
                <div className="p-3.5">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      { label: 'Text Box',     type: 'text'      as const, Icon: Type,        cls: '' },
                      { label: 'Items Table',  type: 'table'     as const, Icon: TableIcon,   cls: '' },
                      { label: 'Vector QR',    type: 'qr'        as const, Icon: QrCode,      cls: '' },
                  

                      { label: 'Ink Stamp',    type: 'stamp'     as const, Icon: CheckCircle, cls: '' },
                      { label: 'Sign Line',    type: 'signature' as const, Icon: Type,        cls: 'bg-sky-50 border-sky-100 text-slate-755' },
                      { label: 'Serial Counter', type: 'serial'  as const, Icon: Hash,        cls: '' },
                      { label: 'Line / Shape', type: 'shape'     as const, Icon: Grid,        cls: '' },
                    ].map(({ label, type, Icon, cls }) => (
                      <button key={type} onClick={() => mobileAddElement(type)}
                        className={`p-3 text-left rounded border border-slate-200 hover:border-blue-300 hover:bg-slate-100/50 transition-all flex items-center gap-2 text-slate-700 ${cls || 'bg-slate-50'}`}>
                        <Icon className="w-4 h-4 text-blue-600" /><span>{label}</span>
                      </button>
                    ))}
                    <button onClick={() => mobileAddElement('stamp', { stampText: 'PAID', stampShape: 'circle', color: '#10b981', stampDraft: true })}
                      className="p-3 bg-emerald-50 text-left rounded border border-emerald-200 hover:border-emerald-300 transition-all flex items-center gap-2 text-emerald-850">
                      <CheckCircle className="w-4 h-4 text-emerald-600" /><span>PAID Stamp</span>
                    </button>
                    <button onClick={() => mobileAddElement('stamp', { stampText: 'UNPAID', stampShape: 'badge', color: '#f97316', stampDraft: true })}
                      className="p-3 bg-amber-50 text-left rounded border border-amber-200 hover:border-amber-300 transition-all flex items-center gap-2 text-amber-850">
                      <CheckCircle className="w-4 h-4 text-amber-600" /><span>UNPAID Stamp</span>
                    </button>
                    <button onClick={() => fileInputRef.current?.click()}
                      className="p-3 bg-slate-50 text-left rounded border border-slate-200 hover:border-blue-300 transition-all flex items-center gap-2 text-slate-700">
                      <ImageIcon className="w-4 h-4 text-blue-600" /><span>Upload Photo</span>
                    </button>
                  </div>
                  <input type="file" ref={fileInputRef} onChange={mobileHandleImageUpload} accept="image/*" className="hidden" />
                </div>
              )}
            </div>

            {/* PANEL: Draw Pen & Theme */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div onClick={() => togglePanel('draw')} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none">
                <div className="flex items-center gap-2">
                  <span className="text-sm">🖌️</span>
                  <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Draw Pen & Theme</span>
                  {isDrawingMode && <span className="text-[8px] bg-rose-600 text-white font-bold px-1.5 py-0.5 rounded animate-pulse">Active</span>}
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${openPanels.draw ? 'rotate-180' : ''}`} />
              </div>
              {openPanels.draw && (
                <div className="p-3.5 space-y-3">
                  {/* Pen tool */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700">Freehand Pencil Tool</span>
                      <button onClick={() => mobileSetDrawingMode(!isDrawingMode)}
                        className={`px-3 py-1.5 rounded text-xs font-bold transition-all flex items-center gap-1.5 ${isDrawingMode ? 'bg-rose-600 hover:bg-rose-700 text-white animate-pulse' : 'bg-purple-700 hover:bg-blue-700 text-white'}`}>
                        {isDrawingMode ? '🛑 Stop Pen' : '🖌️ Draw/Pen'}
                      </button>
                    </div>
                    {isDrawingMode ? (
                      <div className="space-y-2.5 pt-1.5 border-t border-slate-200">
                        <p className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-100 p-2 rounded">
                          👉 Pencil active! Click and drag on the canvas to draw.
                        </p>
                        <div className="flex items-center justify-between gap-1">
                          <label className="text-[10px] font-bold text-slate-500 uppercase">Ink Color</label>
                          <div className="flex items-center gap-1.5">
                            <input type="color" value={currentPenColor} onChange={(e) => mobileSetPenColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                            <input type="text" value={currentPenColor} onChange={(e) => mobileSetPenColor(e.target.value)} className="w-16 bg-white border border-slate-200 text-[10px] p-1 rounded font-mono text-center font-bold text-slate-700" />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Width</span>
                          <div className="flex gap-1.5">
                            {[1, 2, 3, 5, 8].map(w => (
                              <button key={w} onClick={() => mobileSetPenWidth(w)}
                                className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center transition-all ${currentPenWidth === w ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                                {w}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400">Click "Draw/Pen" to sign, highlight, or doodle freehand.</p>
                    )}
                  </div>

                  {/* Theme presets */}
                  <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-3.5 space-y-3">
                    <span className="text-xs font-black text-slate-800 uppercase tracking-wider">Corporate Brand Theme</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {THEME_PRESETS.map(c => {
                        const isSelected = corporateColor.toLowerCase() === c.hex.toLowerCase();
                        return (
                          <button key={c.hex} onClick={() => mobileApplyThemeColor(c.hex)}
                            className={`p-1.5 rounded-lg border text-left flex flex-col justify-between transition-all cursor-pointer ${isSelected ? 'bg-blue-600 border-blue-600 text-white shadow-xs' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full inline-block border shrink-0" style={{ backgroundColor: c.hex, borderColor: isSelected ? '#fff' : '#cbd5e1' }} />
                              <span className={`text-[9.5px] font-bold tracking-tight line-clamp-1 ${isSelected ? 'text-white' : 'text-slate-800'}`}>{c.name}</span>
                            </div>
                            <span className={`text-[7.5px] font-semibold tracking-wide mt-0.5 block ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>{c.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── TAB 2: PAGE SETTINGS ── */}
        {activeTab === 'page' && (
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Invoice Currency</label>
              <CurrencySelector value={activeCurrency} onChange={mobileCurrencyChange} />
              <div className="mt-2 flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Display as:</span>
                <div className="flex items-center bg-slate-100 border border-slate-200 rounded-lg p-0.5 gap-0.5">
                  {(['symbol', 'code'] as const).map(mode => (
                    <button key={mode} onClick={() => mobileCurrencyDisplayModeChange(mode)}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${currencyDisplayMode === mode ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      {mode === 'symbol' ? `Symbol (${getCurrencySymbol(activeCurrency)})` : `Code (${activeCurrency})`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Page Size Target</label>
              <select value={pageSize} onChange={(e) => {
                const size = e.target.value as PageSizePreset;

                // Immediately update the left-sidebar UI.
                setPageSize(size);

                runOrSend(
                  'set-page-size',
                  { size },
                  () => {
                    const oldConfig = PAGE_PRESETS[pageSize];
                    const newConfig = PAGE_PRESETS[size];
                    const scaleX = newConfig.width / oldConfig.width;
                    const scaleY = newConfig.height / oldConfig.height;
                    const scale = Math.min(scaleX, scaleY);

                    const rescaled = elements.map(el => ({
                      ...el,
                      x: Math.round(el.x * scaleX),
                      y: Math.round(el.y * scaleY),
                      width: Math.round(el.width * scale),
                      height: Math.round(el.height * scale),
                      fontSize: el.fontSize
                        ? Math.max(6, Math.round(el.fontSize * scale))
                        : el.fontSize,
                    }));

                    setElements(rescaled);
                    setPageSize(size);
                    saveToHistory(rescaled, size);

                    const availableWidth = window.innerWidth - 640;
                    setZoom(
                      parseFloat(
                        Math.min(
                          1.2,
                          Math.max(
                            0.4,
                            (availableWidth / newConfig.width) * 0.85
                          )
                        ).toFixed(2)
                      )
                    );
                  },
                );
              }}
                className="w-full bg-white border border-slate-200 rounded p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium shadow-sm">
                <option value="A4">A4 Portrait Standard (210 x 297 mm)</option>
                <option value="LETTER">US Letter Document (8.5 x 11 in)</option>
                <option value="LABEL_4X6">4" x 6" Parcel Shipping Label</option>
                <option value="LABEL_3X2">3" x 2" Retail Price Sticker</option>
                <option value="THERMAL_80MM">Thermal Receipt (80mm Roll)</option>
                <option value="THERMAL_58MM">Thermal Receipt (58mm Roll)</option>
              </select>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-slate-400 font-medium">
                  {localStorage.getItem('cloudairambo_default_page_size') === pageSize
                    ? '⭐ This is your default'
                    : `Default: ${PAGE_PRESETS[(localStorage.getItem('cloudairambo_default_page_size') as PageSizePreset) || 'A4']?.name || 'A4'}`}
                </span>
                <button
                  onClick={() => {
                    localStorage.setItem('cloudairambo_default_page_size', pageSize);
                    // force a re-render of this label
                    setActiveTab('page');
                  }}
                  disabled={localStorage.getItem('cloudairambo_default_page_size') === pageSize}
                  className="text-[10px] font-bold px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ⭐ Set as Default
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Page Overflow Behavior</label>
              <select value={pageBehavior} onChange={(e) => {
                const behavior = e.target.value as any;

                // Immediately show selection in sidebar.
                setPageBehavior(behavior);

                runOrSend(
                  'set-page-behavior',
                  { behavior },
                  () => setPageBehavior(behavior),
                );
              }}
                className="w-full bg-white border border-slate-200 rounded p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium shadow-sm">
                <option value="auto_expand">Auto Expand (grows with content)</option>
                <option value="multi_page">Multi Page (adds new pages)</option>
                <option value="fixed">Fixed Size (clips overflow)</option>
              </select>
              <p className="text-[10px] text-slate-400 mt-1 font-medium">
                {pageBehavior === 'auto_expand' && '↕ Canvas grows downward to fit all content automatically.'}
                {pageBehavior === 'multi_page' && '📄 Content splits across multiple A4/Letter pages.'}
                {pageBehavior === 'fixed' && '⚠️ Content outside page bounds will be clipped.'}
              </p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-slate-400 font-medium">
                  {localStorage.getItem('cloudairambo_default_page_behavior') === pageBehavior
                    ? '⭐ This is your default'
                    : `Default: ${(localStorage.getItem('cloudairambo_default_page_behavior') || 'auto_expand').replace('_', ' ')}`}
                </span>
                <button
                  onClick={() => {
                    localStorage.setItem('cloudairambo_default_page_behavior', pageBehavior);
                    setActiveTab('page');
                  }}
                  disabled={localStorage.getItem('cloudairambo_default_page_behavior') === pageBehavior}
                  className="text-[10px] font-bold px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ⭐ Set as Default
                </button>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 space-y-2 text-xs text-slate-600">
              <p className="text-slate-500 font-bold block mb-1">Canvas Grid Specs:</p>
              <div className="flex justify-between font-mono text-[11px]"><span>Width:</span><span className="text-slate-800 font-semibold">{pageConfig.width} px</span></div>
              <div className="flex justify-between font-mono text-[11px]"><span>Height:</span><span className="text-slate-800 font-semibold">{pageConfig.height} px</span></div>
              <div className="flex justify-between font-mono text-[11px]"><span>Snapping:</span><span className="text-emerald-600 font-semibold">{gridSnapping ? '5px Snap Enabled' : 'Free movement'}</span></div>
            </div>

            <button onClick={() => {
              if (!window.confirm('Clear all elements? You can undo this.')) {
                return;
              }

              runOrSend(
                'clear-canvas',
                {},
                () => {
                  setElements([]);
                  saveToHistory([], pageSize);
                },
              );
            }}
              className="w-full py-2.5 px-4 bg-red-50 border border-red-200 text-red-650 rounded hover:bg-red-100 hover:text-red-700 transition-colors text-xs font-semibold">
              Clear Entire Canvas
            </button>
          </div>
        )}

        {/* ── TAB 3: GUIDE ── */}
        {activeTab === 'guide' && (
          <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
            <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100">
              <h4 className="font-bold text-slate-800 mb-1">💡 Quick Design Tip</h4>
              <p>Click elements directly on the canvas to drag, scale, or stack them.</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-bold text-slate-700">Available Controls:</h4>
              <ul className="list-disc pl-4 space-y-1.5 text-slate-500">
                <li><strong className="text-slate-700">Double-Click Text:</strong> Edits text directly inline.</li>
                <li><strong className="text-slate-700">Opacity Slider:</strong> Set watermarks by dragging element opacity down.</li>
                <li><strong className="text-slate-700">Custom Columns:</strong> Click a table then add, rename, or delete columns in the properties bar.</li>
                <li><strong className="text-slate-700">Stamp Distress:</strong> Toggle vintage distress mode on stamps for an authentic look.</li>
                <li><strong className="text-slate-700">Keyboard Shortcuts:</strong>{' '}
                  <span className="bg-slate-100 border border-slate-200 rounded px-1 font-mono text-[10px]">Ctrl+C</span>{' / '}
                  <span className="bg-slate-100 border border-slate-200 rounded px-1 font-mono text-[10px]">Ctrl+V</span> copy/paste,{' '}
                  <span className="bg-slate-100 border border-slate-200 rounded px-1 font-mono text-[10px]">Ctrl+Z</span>{' / '}
                  <span className="bg-slate-100 border border-slate-200 rounded px-1 font-mono text-[10px]">Ctrl+Y</span> undo/redo.
                </li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </aside>
    </>
  );
};

export default LeftSidebar;
